import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

export type ArchitectureMode = 'elevation' | 'interior';

import { ArchitectureTask, TaskType, TaskStatus } from '../types/architectureTask';
import { WORKFLOWS, DEFAULT_TASK_TYPE } from '../data/workflows';

export interface ArchitectureState {
    // Mode
    mode: ArchitectureMode;
    setMode: (mode: ArchitectureMode) => void;

    // Project Context
    projectType: string;
    setProjectType: (type: string) => void;

    // Global Style
    selectedPaletteId: string | null;
    setSelectedPaletteId: (id: string | null) => void;

    // UI State
    isControlPanelOpen: boolean;
    toggleControlPanel: () => void;

    isRightPanelOpen: boolean; // Added for collapsible right panel
    toggleRightPanel: () => void;

    // Selection/Lasso State
    isLassoActive: boolean;
    toggleLasso: (isActive: boolean) => void;

    // Actions
    resetWorkspace: () => void;

    // Future-proofing: Selected assets for placement
    selectedAssets: string[]; // IDs of selected assets
    toggleAssetSelection: (assetId: string) => void;

    // Regional Identity (Salmani Workbench)
    selectedRegionId: string | null;
    setSelectedRegionId: (id: string | null) => void;

    // Architectural Style (New)
    selectedStyle: 'Traditional' | 'Transitional' | 'Contemporary';
    setSelectedStyle: (style: 'Traditional' | 'Transitional' | 'Contemporary') => void;

    // Style Matrix (New)
    selectedStyleMatrixId: string | null;
    setSelectedStyleMatrixId: (id: string | null) => void;

    // Time of Day (Render Bridge)
    timeOfDay: string;
    setTimeOfDay: (time: string) => void;

    // Interior Design Parameters
    roomType: string;
    setRoomType: (type: string) => void;
    doorCount: number;
    setDoorCount: (count: number) => void;
    windowCount: number;
    setWindowCount: (count: number) => void;
    interiorTheme: 'classic' | 'modern' | 'traditional';
    setInteriorTheme: (theme: 'classic' | 'modern' | 'traditional') => void;
    interiorColorPaletteId: string | null;
    setInteriorColorPaletteId: (id: string | null) => void;

    // Style Transformer State
    sourceImage: string | null; // Base64 or URL
    setSourceImage: (image: string | null) => void;

    resultImage: string | null; // Base64 or URL
    setResultImage: (image: string | null) => void;

    gallery: string[]; // History of generated images
    clearGallery: () => void;

    isProcessing: boolean;
    setIsProcessing: (isProcessing: boolean) => void;

    lastError: string | null;
    setLastError: (error: string | null) => void;

    // --- Task Engine ---
    activeTask: ArchitectureTask | null;
    startTask: (type: TaskType, initialData?: any) => void;
    updateTaskData: (data: any) => void;
    nextStep: () => void;
    prevStep: () => void;
    completeTask: () => void;
    cancelTask: () => void;
}

export const useArchitectureStore = create<ArchitectureState>()(
    immer((set) => ({
        mode: 'elevation',
        setMode: (mode) =>
            set((state) => {
                state.mode = mode;
                // Reset interior parameters when switching back to elevation
                if (mode === 'elevation') {
                    state.roomType = 'living_room';
                    state.doorCount = 2;
                    state.windowCount = 3;
                    state.interiorTheme = 'modern';
                    state.interiorColorPaletteId = null;
                }
            }),

        projectType: 'Residential Villa',
        setProjectType: (type) =>
            set((state) => {
                state.projectType = type;
            }),

        selectedPaletteId: null,
        setSelectedPaletteId: (id) =>
            set((state) => {
                state.selectedPaletteId = id;
            }),

        isControlPanelOpen: true,
        toggleControlPanel: () =>
            set((state) => {
                state.isControlPanelOpen = !state.isControlPanelOpen;
            }),

        isRightPanelOpen: false, // Default to closed as per user request ("collapsable by default")
        toggleRightPanel: () =>
            set((state) => {
                state.isRightPanelOpen = !state.isRightPanelOpen;
            }),

        isLassoActive: false,
        toggleLasso: (isActive) =>
            set((state) => {
                state.isLassoActive = isActive;
            }),

        resetWorkspace: () =>
            set((state) => {
                state.mode = 'elevation';
                state.selectedPaletteId = null;
                state.selectedAssets = [];
                state.roomType = 'living_room';
                state.doorCount = 2;
                state.windowCount = 3;
                state.interiorTheme = 'modern';
                state.interiorColorPaletteId = null;
            }),

        selectedAssets: [],
        toggleAssetSelection: (assetId) =>
            set((state) => {
                const index = state.selectedAssets.indexOf(assetId);
                if (index !== -1) {
                    state.selectedAssets.splice(index, 1);
                } else {
                    state.selectedAssets.push(assetId);
                }
            }),

        selectedRegionId: 'northern_najdi',
        setSelectedRegionId: (id) =>
            set((state) => {
                state.selectedRegionId = id;
            }),

        selectedStyle: 'Traditional',
        setSelectedStyle: (style) =>
            set((state) => {
                state.selectedStyle = style;
            }),

        selectedStyleMatrixId: null,
        setSelectedStyleMatrixId: (id) =>
            set((state) => {
                state.selectedStyleMatrixId = id;
            }),

        timeOfDay: 'Day',
        setTimeOfDay: (time) =>
            set((state) => {
                state.timeOfDay = time;
            }),

        // Interior parameters
        roomType: 'living_room',
        setRoomType: (type) =>
            set((state) => {
                state.roomType = type;
            }),

        doorCount: 2,
        setDoorCount: (count) =>
            set((state) => {
                state.doorCount = count;
            }),

        windowCount: 3,
        setWindowCount: (count) =>
            set((state) => {
                state.windowCount = count;
            }),

        interiorTheme: 'modern',
        setInteriorTheme: (theme) =>
            set((state) => {
                state.interiorTheme = theme;
            }),

        interiorColorPaletteId: null,
        setInteriorColorPaletteId: (id) =>
            set((state) => {
                state.interiorColorPaletteId = id;
            }),

        sourceImage: null,
        setSourceImage: (image) =>
            set((state) => {
                state.sourceImage = image;
            }),

        resultImage: null,
        gallery: [], // Initialize gallery
        setResultImage: (image) =>
            set((state) => {
                state.resultImage = image;
                // Add to gallery if it's a new image
                if (image && !state.gallery.includes(image)) {
                    state.gallery.unshift(image); // Add to beginning (newest first)
                }
            }),

        // Action to clear gallery
        clearGallery: () =>
            set((state) => {
                state.gallery = [];
            }),

        isProcessing: false,
        setIsProcessing: (isProcessing) =>
            set((state) => {
                state.isProcessing = isProcessing;
                if (isProcessing) {
                    state.lastError = null; // Clear error on new process
                }
            }),

        lastError: null,
        setLastError: (error) =>
            set((state) => {
                state.lastError = error;
            }),

        // --- Task Engine Implementation ---
        activeTask: null,

        startTask: (type, initialData = {}) =>
            set((state) => {
                const workflow = WORKFLOWS[type];
                if (!workflow) {
                    console.error(`Workflow ${type} not found`);
                    return;
                }

                state.activeTask = {
                    id: crypto.randomUUID(),
                    type,
                    status: 'in_progress',
                    step: 0,
                    data: initialData,
                    history: [],
                    createdAt: Date.now(),
                    updatedAt: Date.now(),
                };

                // Sync legacy state for backward compatibility (optional but helpful)
                // e.g., if starting Style Transformer, clear old sourceImage
                if (type === 'STYLE_TRANSFORMER') {
                    state.sourceImage = null;
                    state.resultImage = null;
                }
            }),

        updateTaskData: (data) =>
            set((state) => {
                if (state.activeTask) {
                    state.activeTask.data = { ...state.activeTask.data, ...data };
                    state.activeTask.updatedAt = Date.now();

                    // Sync legacy state if applicable (e.g. for Canvas display)
                    if (data.sourceImage) state.sourceImage = data.sourceImage;
                    if (data.resultImage) state.resultImage = data.resultImage;
                }
            }),

        nextStep: () =>
            set((state) => {
                if (state.activeTask) {
                    const workflow = WORKFLOWS[state.activeTask.type];
                    if (state.activeTask.step < workflow.steps.length - 1) {
                        state.activeTask.step += 1;
                    } else {
                        // Task Complete?
                        state.activeTask.status = 'completed';
                    }
                }
            }),

        prevStep: () =>
            set((state) => {
                if (state.activeTask && state.activeTask.step > 0) {
                    state.activeTask.step -= 1;
                }
            }),

        completeTask: () =>
            set((state) => {
                if (state.activeTask) {
                    state.activeTask.status = 'completed';
                    // Here we could archive the task or move result to project assets
                }
            }),

        cancelTask: () =>
            set((state) => {
                state.activeTask = null;
                // Reset legacy state
                state.sourceImage = null;
                state.resultImage = null;
            }),
    }))
);